
import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { fetchOrganisationData } from '@/utils/api';

export type FilterOperation = 'equals' | 'contains' | 'startsWith' | 'endsWith' | 'gt' | 'lt' | 'gte' | 'lte' | 'not_equals' | 'like' | 'not_like' | 'is_blank' | 'is_not_blank' | 'between';

interface FilterConfig {
  column: string;
  value: string;
  operation: FilterOperation;
}

interface PaginationInfo {
  totalRows: number;
  totalPages: number;
  currentPage: number;
  pageSize: number;
}

interface UseConnectionDataParams {
  connectionId: string | null;
  selectedTable: { schema: string; tableName: string } | null;
  organisationId?: string;
  connection?: any;
  initialAvailableTables?: any[];
  externalData?: any[] | null;
  externalFilters?: FilterConfig[] | null;
  page?: number;
  pageSize?: number;
  onPaginationChange?: (paginationInfo: PaginationInfo) => void;
  fileDescriptionFilter?: string | null;
  effectiveOrgId?: string;
  dataCutoffDate?: string;
}

export const useConnectionData = ({
  connectionId,
  selectedTable,
  organisationId,
  connection,
  initialAvailableTables = [],
  externalData = null,
  externalFilters = null,
  page = 1,
  pageSize = 100,
  onPaginationChange,
  fileDescriptionFilter = null,
  effectiveOrgId,
  dataCutoffDate,
}: UseConnectionDataParams) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [tableData, setTableData] = useState<any[]>([]);
  const [initialTableData, setInitialTableData] = useState<any[]>([]);
  const [columns, setColumns] = useState<string[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortConfig, setSortConfig] = useState<{ key: string; direction: 'asc' | 'desc' } | null>(null);
  const [availableTables, setAvailableTables] = useState<any[]>(initialAvailableTables);
  const [activeFilters, setActiveFilters] = useState<FilterConfig[]>([]);
  const [hasAppliedExternalFilters, setHasAppliedExternalFilters] = useState(false);
  const [paginationData, setPaginationData] = useState<PaginationInfo>({
    totalRows: 0,
    totalPages: 1,
    currentPage: page,
    pageSize: pageSize
  });
  const [shouldRefetch, setShouldRefetch] = useState(false);

  const prevExternalDataRef = useRef<any[] | null>(null);
  const prevExternalFiltersRef = useRef<FilterConfig[] | null>(null);
  const prevSelectedTableRef = useRef<{ schema: string; tableName: string } | null>(null);
  const prevPageRef = useRef<number>(page);
  const prevPageSizeRef = useRef<number>(pageSize);
  const currentSelectedTableRef = useRef<{ schema: string; tableName: string } | null>(selectedTable);


  useEffect(() => {
    setAvailableTables(initialAvailableTables);
  }, [initialAvailableTables]);

  const setExternalData = useCallback((data: any[]) => {
    if (!data || data.length === 0) return;

    if (prevExternalDataRef.current &&
      JSON.stringify(prevExternalDataRef.current[0]) === JSON.stringify(data[0]) &&
      prevExternalDataRef.current.length === data.length) {
      return;
    }

    prevExternalDataRef.current = data;
    setTableData(data);
    setInitialTableData(data);

    if (data.length > 0) {
      setColumns(Object.keys(data[0]));
      setError(null);
    }
  }, []);

  const setExternalFilters = useCallback((filters: FilterConfig[] | null) => {
    prevExternalFiltersRef.current = filters;

    if (filters && filters.length > 0) {
      setActiveFilters(filters);
      setHasAppliedExternalFilters(true);
    } else if (filters === null) {
      setActiveFilters([]);
      setHasAppliedExternalFilters(false);
    }
  }, []);

  useEffect(() => {
    if (externalData && externalData.length > 0) {
      setExternalData(externalData);
      setLoading(false);
    }
  }, [externalData, setExternalData]);

  useEffect(() => {
    setExternalFilters(externalFilters);
  }, [externalFilters, setExternalFilters]);

  const fetchData = useCallback(async () => {
    const currentTable = currentSelectedTableRef.current;
    if (!connectionId || !currentTable) {
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Build filters array.
      // Inventory snapshot tables are keyed by connection_id, while generic CSV tables
      // use file_description. Route the implicit filter accordingly.
      const allFilters = [...(activeFilters.length > 0 ? activeFilters : [])];
      let hasImplicitFileFilter = false;
      if (fileDescriptionFilter) {
        const isInventoryTable = currentTable.tableName?.includes('inventory_snapshots');
        allFilters.push({
          column: isInventoryTable ? 'connection_id' : 'file_description',
          value: fileDescriptionFilter,
          operation: 'equals' as FilterOperation
        });
        hasImplicitFileFilter = true;
      }

      // Use pagination parameters in the API call with sorting
      let data = await fetchOrganisationData(
        connectionId,
        currentTable.schema,
        currentTable.tableName,
        {
          page,
          pageSize,
          filters: allFilters.length > 0 ? allFilters : undefined,
          sortColumn: sortConfig?.key,
          sortDirection: sortConfig?.direction,
          organisationId,
          connection,
          effectiveOrgId,
          dataCutoffDate,
        }
      );

      // Some CSV imports can have a file_description mismatch (renamed files/normalization).
      // If the implicit filter returns no rows, retry once without it to avoid false empty states.
      if ((!data || data.length === 0) && hasImplicitFileFilter) {
        data = await fetchOrganisationData(
          connectionId,
          currentTable.schema,
          currentTable.tableName,
          {
            page,
            pageSize,
            filters: activeFilters.length > 0 ? activeFilters : undefined,
            sortColumn: sortConfig?.key,
            sortDirection: sortConfig?.direction,
            organisationId,
            connection,
            effectiveOrgId,
            dataCutoffDate,
          }
        );
      }

      if (data && data.length > 0) {
        setColumns(Object.keys(data[0]));
        setTableData(data);
        setInitialTableData(data);

        // Check for pagination metadata in the response
        const paginationInfo = (data as any).pagination || {};
        const newPaginationData = {
          totalRows: paginationInfo.totalRows || 0,
          totalPages: paginationInfo.totalPages || 1,
          currentPage: paginationInfo.currentPage || page,
          pageSize: paginationInfo.pageSize || pageSize
        };

        setPaginationData(newPaginationData);

        if (onPaginationChange) {
          onPaginationChange(newPaginationData);
        }
      } else {
        setColumns([]);
        setTableData([]);
        setInitialTableData([]);

        setError('No data found in the selected table');
      }
    } catch (error: any) {
      setError(error.message || 'Failed to load data from connected source');
      setTableData([]);
      setInitialTableData([]);
      setColumns([]);
    } finally {
      setLoading(false);
    }
  }, [connectionId, page, pageSize, activeFilters, sortConfig, onPaginationChange, organisationId, connection, fileDescriptionFilter, effectiveOrgId, dataCutoffDate]);

  const refreshTableData = useCallback(() => {
    if (initialTableData.length > 0) {
      setTableData([...initialTableData]);
    } else {
      fetchData();
    }
  }, [initialTableData]);

  // Refresh data when pagination params or sort changes
  useEffect(() => {
    if (prevPageRef.current !== page || prevPageSizeRef.current !== pageSize) {
      prevPageRef.current = page;
      prevPageSizeRef.current = pageSize;

      if (connectionId && selectedTable) {
        fetchData();
      }
    }
  }, [page, pageSize, connectionId, selectedTable]);

  // Trigger refetch when sort changes for database queries
  useEffect(() => {
    if (connectionId && selectedTable && !externalData) {
      fetchData();
    }
  }, [sortConfig]);

  useEffect(() => {
    // Update the ref FIRST, before any other logic
    currentSelectedTableRef.current = selectedTable;

    if (!connectionId || !selectedTable) {
      setColumns([]);
      setTableData([]);
      setInitialTableData([]);
      setActiveFilters([]);
      setSortConfig(null);
      return;
    }

    // Clear filters and sort when changing tables to prevent stale state
    setActiveFilters([]);
    setSortConfig(null);
    setHasAppliedExternalFilters(false);

    // Trigger fetch after clearing state
    setShouldRefetch(true);
  }, [selectedTable, connectionId]);

  // Safety net: ensure we always fetch once a table is selected.
  // This prevents "list tables only" states when shouldRefetch is missed.
  useEffect(() => {
    if (!connectionId || !selectedTable || externalData) {
      return;
    }

    const tableChanged =
      prevSelectedTableRef.current?.schema !== selectedTable.schema ||
      prevSelectedTableRef.current?.tableName !== selectedTable.tableName;
    const firstLoadForTable = tableData.length === 0 && !loading && !error;

    if (tableChanged || firstLoadForTable) {
      prevSelectedTableRef.current = selectedTable;
      fetchData();
    }
  }, [
    connectionId,
    selectedTable?.schema,
    selectedTable?.tableName,
    externalData,
    tableData.length,
    loading,
    error,
    fetchData,
  ]);

  const handleSort = (key: string) => {
    let direction: 'asc' | 'desc' = 'asc';

    if (sortConfig && sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    } else if (sortConfig && sortConfig.key === key && sortConfig.direction === 'desc') {
      setSortConfig(null);
      return;
    }

    setSortConfig({ key, direction });
  };

  const getDisplayTableName = (fullTableName: string) => {
    if (['product', 'products'].includes(fullTableName.toLowerCase())) {
      return fullTableName.charAt(0).toUpperCase() + fullTableName.slice(1);
    }

    const parts = fullTableName.split('__');
    if (parts.length > 1) {
      return parts
        .map(part => part.charAt(0).toUpperCase() + part.slice(1))
        .join(' ');
    }

    return fullTableName.charAt(0).toUpperCase() + fullTableName.slice(1);
  };

  const addFilter = useCallback((column: string, value: string, operation: FilterOperation = 'equals') => {
    const existingFilterIndex = activeFilters.findIndex(
      filter => filter.column === column && filter.value === value && filter.operation === operation
    );

    if (existingFilterIndex !== -1) {
      const newFilters = [...activeFilters];
      newFilters.splice(existingFilterIndex, 1);
      setActiveFilters(newFilters);
      setHasAppliedExternalFilters(false);
      return;
    }

    setActiveFilters([...activeFilters, { column, value, operation }]);
  }, [activeFilters]);

  // Trigger refetch when filters change for database queries
  useEffect(() => {
    if (connectionId && selectedTable && !externalData && activeFilters.length > 0) {
      fetchData();
    }
  }, [activeFilters, fetchData, connectionId, selectedTable, externalData]);

  // Handle refetch after filters are cleared
  useEffect(() => {
    if (shouldRefetch) {
      setShouldRefetch(false);
      fetchData();
    }
  }, [shouldRefetch, fetchData]);

  const clearFilters = useCallback(() => {
    setActiveFilters([]);
    setHasAppliedExternalFilters(false);
    setSearchTerm('');
    setSortConfig(null);

    // For database queries, trigger refetch; for external data, reset to initial data
    if (externalData && initialTableData.length > 0) {
      setTableData(initialTableData);
    } else if (connectionId && currentSelectedTableRef.current) {
      setShouldRefetch(true);
    }
  }, [initialTableData, externalData, connectionId]);

  const getFilterValue = (column: string): string => {
    const filter = activeFilters.find(f => f.column === column);
    return filter ? filter.value : '';
  };

  const filteredAndSortedData = useMemo(() => {
    let result = [...tableData];

    // Only apply client-side filtering and sorting for external data (from chatbot)
    // For database queries, these operations are handled server-side
    if (externalData && activeFilters.length > 0) {
      result = result.filter(item => {
        return activeFilters.every(filter => {
          const columnValue = item[filter.column];
          if (columnValue === null || columnValue === undefined) {
            if (filter.operation === 'is_blank') return true;
            if (filter.operation === 'is_not_blank') return false;
            return false; // Any other operation on null is false
          }

          const isComparisonFilter = ['gt', 'lt', 'gte', 'lte', 'equals', 'not_equals'].includes(filter.operation);
          const filterValue = filter.value;

          if (isComparisonFilter && item[filterValue] !== undefined) {
            const compareColumnValue = item[filterValue];

            const numColumnValue = parseFloat(columnValue);
            const numCompareValue = parseFloat(compareColumnValue);

            if (!isNaN(numColumnValue) && !isNaN(numCompareValue)) {
              switch (filter.operation) {
                case 'gt':
                  return numColumnValue > numCompareValue;
                case 'lt':
                  return numColumnValue < numCompareValue;
                case 'gte':
                  return numColumnValue >= numCompareValue;
                case 'lte':
                  return numColumnValue <= numCompareValue;
                case 'equals':
                  return numColumnValue === numCompareValue;
                case 'not_equals':
                  return numColumnValue !== numCompareValue;
                default:
                  return false;
              }
            }
          }

          const cellValue = String(columnValue || '').toLowerCase();
          const filterValueLower = filter.value.toLowerCase();

          switch (filter.operation) {
            case 'contains':
              return cellValue.includes(filterValueLower);
            case 'startsWith':
              return cellValue.startsWith(filterValueLower);
            case 'endsWith':
              return cellValue.endsWith(filterValueLower);
            case 'gt':
              return parseFloat(cellValue) > parseFloat(filterValueLower);
            case 'lt':
              return parseFloat(cellValue) < parseFloat(filterValueLower);
            case 'gte':
              return parseFloat(cellValue) >= parseFloat(filterValueLower);
            case 'lte':
              return parseFloat(cellValue) <= parseFloat(filterValueLower);
            case 'not_equals':
              return cellValue !== filterValueLower;
            case 'like':
              return cellValue.includes(filterValueLower);
            case 'not_like':
              return !cellValue.includes(filterValueLower);
            case 'is_blank':
              return cellValue === '';
            case 'is_not_blank':
              return cellValue !== '';
            case 'equals':
            default:
              return cellValue === filterValueLower;
          }
        });
      });
    }

    if (searchTerm.trim()) {
      result = result.filter(item =>
        Object.values(item).some(value =>
          String(value).toLowerCase().includes(searchTerm.toLowerCase())
        )
      );
    }

    // Only apply client-side sorting for external data (from chatbot)
    // For database queries, sorting is handled server-side
    if (externalData && sortConfig) {
      result.sort((a, b) => {
        const aValue = a[sortConfig.key];
        const bValue = b[sortConfig.key];

        if (typeof aValue === 'number' && typeof bValue === 'number') {
          return sortConfig.direction === 'asc'
            ? aValue - bValue
            : bValue - aValue;
        }

        const aString = String(aValue).toLowerCase();
        const bString = String(bValue).toLowerCase();

        if (aString < bString) {
          return sortConfig.direction === 'asc' ? -1 : 1;
        }
        if (aString > bString) {
          return sortConfig.direction === 'asc' ? 1 : -1;
        }
        return 0;
      });
    }

    return result;
  }, [tableData, searchTerm, sortConfig, activeFilters, externalData]);

  return {
    loading,
    error,
    columns,
    tableData,
    filteredAndSortedData,
    searchTerm,
    setSearchTerm,
    sortConfig,
    handleSort,
    availableTables,
    getDisplayTableName,
    activeFilters,
    addFilter,
    clearFilters,
    getFilterValue,
    setExternalData,
    setExternalFilters,
    refreshTableData,
    initialTableData,
    paginationData
  };
};
